import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, MessageCircle, Send } from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '../auth/authStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { openSupportChatwoot } from './chatwoot';
import { FaqPanel } from './faq/FaqPanel';
import { copyWithFallback, supportBlockVisibility } from './supportBlocks';
import { fetchSupportConfig } from './supportConfig';

/**
 * The Help tab: four blocks, top to bottom, deliberately built as three
 * visual tiers — colored card → neutral card → no card at all
 * (doc/SUPPORT_CENTER_PLAN.md §0.2). The chat card must stay cyan, never
 * gold (gold means money, DESIGN_SYSTEM.md §1.2) and the Telegram card must
 * stay neutral grey, never Telegram's brand blue (§1.3).
 */
export function HelpPanel({ onGoToFeedback }: { onGoToFeedback: () => void }) {
  const t = useI18nStore((state) => state.t);
  const language = useI18nStore((state) => state.language);
  const user = useAuthStore((state) => state.user);

  const configQuery = useQuery({
    queryKey: ['support-config'],
    queryFn: fetchSupportConfig,
    staleTime: 5 * 60 * 1000,
  });
  const config = configQuery.data;
  const visibility = supportBlockVisibility(config);

  const [opening, setOpening] = useState(false);
  const [chatError, setChatError] = useState(false);

  async function openChat() {
    setOpening(true);
    setChatError(false);
    try {
      await openSupportChatwoot({ user, language, route: 'avisos.help' });
    } catch {
      // Chatwoot can reject with CHATWOOT_LOAD_FAILED or CHATWOOT_TIMEOUT —
      // either way the player still needs a way forward, not a dead end.
      setChatError(true);
    } finally {
      setOpening(false);
    }
  }

  return (
    <div className="help-panel">
      {/* 1. Live chat — the one colored card on this screen. Fixed 24/7 copy,
          no online/offline branch (product decision: the service really is
          round the clock). */}
      <button type="button" className="support-chat-card" disabled={opening} onClick={openChat}>
        <MessageCircle aria-hidden="true" />
        <span>
          <strong>{opening ? t('supportOpening') : t('helpChat')}</strong>
          <small>{t('supportChat24h')}</small>
        </span>
      </button>

      {chatError ? (
        <p className="support-chat-error" role="alert">
          <AlertTriangle size={14} aria-hidden="true" />
          <span>
            {t('supportChatError')}{' '}
            <button type="button" onClick={onGoToFeedback}>
              {t('feedbackNew')}
            </button>
          </span>
        </p>
      ) : null}

      {/* 2. FAQ — dropped in unchanged. */}
      <FaqPanel />

      {/* 3. Telegram channel — neutral card, only when ops has configured a
          URL for this country. */}
      {visibility.telegram && config ? (
        <button
          type="button"
          className="support-tg-card"
          onClick={() => window.open(config.telegram_channel_url, '_blank', 'noopener,noreferrer')}
        >
          <Send aria-hidden="true" />
          <span>
            <strong>{t('tgChannelTitle')}</strong>
            <small>{t('tgChannelSub')}</small>
          </span>
        </button>
      ) : null}

      {/* 4. Business contact — no card, only when ops has configured an
          e-mail for this country. */}
      {visibility.business && config ? <BusinessFooter email={config.business_email} /> : null}
    </div>
  );
}

function BusinessFooter({ email }: { email: string }) {
  const t = useI18nStore((state) => state.t);
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    await copyWithFallback(email, {
      clipboardWrite: navigator.clipboard ? (text) => navigator.clipboard.writeText(text) : null,
      // navigator.clipboard is unavailable on non-HTTPS origins and older
      // in-app WebViews — exactly where this app runs — so mailto: alone
      // (which frequently opens nothing on mobile) can't be the only path.
      fallbackCopy: (text) => {
        const input = document.createElement('textarea');
        input.value = text;
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      },
    });
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="support-business-footer">
      <p className="support-business-title">{t('businessTitle')}</p>
      <p className="support-business-sub">{t('businessSub')}</p>
      <div className="support-business-row">
        <a className="support-business-email" href={`mailto:${email}`}>
          {email}
        </a>
        <button type="button" className="support-business-copy" onClick={handleCopy}>
          {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          {copied ? t('businessCopied') : t('businessCopy')}
        </button>
      </div>
    </div>
  );
}
